import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { CompleteProfileProvider, useCompleteProfile } from "@/context/CompleteProfileContext";
import CompleteProfileProgress from "@/components/complete-profile/CompleteProfileProgress";
import BioStep from "@/components/complete-profile/steps/BioStep";
import CareerStep from "@/components/complete-profile/steps/CareerStep";
import AppearanceStep from "@/components/complete-profile/steps/AppearanceStep";
import LifestyleStep from "@/components/complete-profile/steps/LifestyleStep";
import RelationshipsStep from "@/components/complete-profile/steps/RelationshipsStep";
import PhotosCompleteStep from "@/components/complete-profile/steps/PhotosCompleteStep";
import CompletionStep from "@/components/complete-profile/steps/CompletionStep";
import { ArrowLeft, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { businessRulesEngine } from "@/core/BusinessRulesEngine";

const CompleteProfileSteps = () => {
  const navigate = useNavigate();
  const { profileData, loading: authLoading } = useAuth();

  const handleClose = () => {
    navigate('/main/feed', { replace: true });
  };
  const { currentStep, requiredSteps, prevStep, loading } = useCompleteProfile();

  // Check if profile is already complete
  React.useEffect(() => {
    if (!authLoading && profileData) {
      const validation = businessRulesEngine.validateProfileComplete(profileData);
      console.log('[CompleteProfile] Validation result:', {
        allowed: validation.allowed,
        reason: validation.reason,
        data: validation.data,
        photoCount: profileData?.profile_photos?.length,
        bioScore: (profileData as any)?.bio_score,
        bio: profileData?.bio?.substring(0, 50)
      });
      if (validation.allowed) {
        navigate('/main/feed', { replace: true });
      }
    }
  }, [authLoading, profileData, navigate]);

  if (loading || authLoading || requiredSteps.length === 0) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-luxury-pinkBright border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profileData) {
    navigate('/auth', { replace: true });
    return null;
  }

  const currentStepInfo = requiredSteps[currentStep - 1];

  const renderStep = () => {
    if (!currentStepInfo) return <CompletionStep />;
    
    switch (currentStepInfo.id) {
      case 'bio':
        return <BioStep />;
      case 'career':
        return <CareerStep />;
      case 'appearance':
        return <AppearanceStep />;
      case 'lifestyle':
        return <LifestyleStep />;
      case 'relationships':
        return <RelationshipsStep />;
      case 'photos':
        return <PhotosCompleteStep />;
      case 'completion':
        return <CompletionStep />;
      default:
        return <CompletionStep />;
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Sticky Header */}
      <div className="sticky top-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            {/* Back Button */}
            {currentStep > 1 ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={prevStep}
                className="text-white/70 hover:text-white hover:bg-white/10 flex-shrink-0"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/main/feed')}
                className="text-white/70 hover:text-white hover:bg-white/10 flex-shrink-0"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}

            {/* Progress Bar */}
            <CompleteProfileProgress />

            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="text-white/70 hover:text-white hover:bg-white/10 flex-shrink-0"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Step Title */}
        {currentStepInfo && currentStepInfo.id !== 'completion' && (
          <div className="mb-2 text-center">
            <p className="text-luxury-pinkBright text-sm font-medium">
              Complete seu perfil em poucos minutos ⚡
            </p>
          </div>
        )}

        {/* Step Content */}
        {renderStep()}
      </div>
    </div>
  );
};

const CompleteProfile = () => {
  return (
    <CompleteProfileProvider>
      <CompleteProfileSteps />
    </CompleteProfileProvider>
  );
};

export default CompleteProfile;
